import { useRouter } from 'next/router'
import { isEmpty } from 'ramda'
import { gql, useQuery } from '@apollo/client'
import { Box, useDialogController } from '@machobear/design-system'
import { motion } from 'framer-motion'

import { breakpoints } from '@/styles'
import { useCityData, useNeighbourhoodData } from '@/lib/locations'
import { getFilterSlugData, getUrlSlugs } from '@/utils/location'
import { useAlertModalContext } from '@/hooks/use-alert-modal-context'
import { useFilters } from '@/hooks/use-filters'
import useMediaQuery from '@/hooks/use-media-query'
import useSessionComplete from '@/hooks/use-session-complete'
import useCreateListingAlert from '@/features/Listings/hooks/use-create-listing-alert'
import useListingsFavourites from '@/features/Listings/hooks/use-listings-favourites'
import { injectAds, useResponsiveAdValues } from '@/components/adsense'
import { AlertConfirmationModal } from '@/components/alert'

import { useMapList } from '../maplist-context'
import { List as ListingsList } from './list'

import 'react-indiana-drag-scroll/dist/style.css'

const BUILDING_DATA = gql`
  fragment mapBuildingListFields on Listing {
    bathrooms
    bedrooms
    gr_min_price
    gr_max_price
    gr_unit
    gr_count
    allow_applications
    public_viewing
    hide_unit_number
    unit_type_txt_id
    unit_type_scope_txt_id
    cover_photo_aws_s3_key
    listing_id
    price
    price_frequency
    is_hidden
    state_machine
    verified_state_machine
    furnished
    size
    unit_files {
      aws_s3_key
      position
      tag
    }
    landlords {
      identity_verified_state
    }
    search_deals
    previous_price
    price_change_status
  }
  fragment mapListViewFields on FullListing {
    street_name
    building_type
    building_name
    building_id
    full_street_name
    city
    state
    zip
    location {
      lat
      lon
    }
    bathrooms
    bedrooms
    size
    gr_min_size
    gr_min_price
    gr_max_price
    allow_applications
    public_viewing
    gr_unit
    hide_unit_number
    unit_type_txt_id
    unit_type_scope_txt_id
    cover_photo_aws_s3_key
    listing_id
    price
    price_frequency
    is_hidden
    state_machine
    verified_state_machine
    furnished
    unit_files {
      aws_s3_key
      position
      tag
    }
    landlords {
      identity_verified_state
    }
    search_deals
    previous_price
    price_change_status
  }
  query ($input: ListSearchInput!) {
    listSearch {
      buildings(input: $input) {
        metadata {
          page
          page_size
          total_count
          listings_count
        }
        feed {
          building_id
          street_name
          building_type
          full_street_name
          city
          state
          zip
          building_name
          location {
            lat
            lon
          }
          building_files {
            aws_s3_key
            tag
          }
          listings {
            ...mapBuildingListFields
          }
          listing_count
        }
      }
      featured(input: $input) {
        ...mapListViewFields
      }
    }
  }
`

const MapList = ({
  boundaries,
  onlyVerifiedListings,
  markersLoaded,
  customSearchCityName,
  ...rest
}) => {
  const { showModal, closeModal } = useDialogController()
  const { isListVisible } = useMapList()
  const { modalVisible, setModalVisible } = useAlertModalContext()
  useListingsFavourites()
  useCreateListingAlert(showModal)
  const isLoggedIn = useSessionComplete()
  const [filters, setFilter, , isFilterSet] = useFilters()
  const router = useRouter()
  const { citySlug, neighbourhoodSlug } = getUrlSlugs(router)
  const neighborhood = useNeighbourhoodData(neighbourhoodSlug, citySlug)
  const { data: cityData, loading: cityLoading } = useCityData(citySlug)
  const filterSlugData = getFilterSlugData({ router })
  const filterSlug = filterSlugData?.slug
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const { adInterval } = useResponsiveAdValues()

  const buildingDataInput = {
    ...filters,
    ...(onlyVerifiedListings ? { search_only_verified: true } : {}),
    ...(customSearchCityName ? { cities: [customSearchCityName] } : {}),
    ...boundaries,
    page_size: 48
  }

  const {
    previousData = {},
    data = previousData,
    loading: loadingBuildings
  } = useQuery(BUILDING_DATA, {
    skip: !isListVisible && isEmpty(boundaries),
    variables: {
      input: buildingDataInput
    },
    pollInterval: 0,
    fetchPolicy: 'cache-first'
  })

  const {
    listSearch: {
      buildings: { feed: buildings = [], metadata = {} } = {},
      featured
    } = {}
  } = data

  const listAnimation = {
    hidden: {
      opacity: 0,
      display: 'none'
    },
    show: { opacity: 1, display: 'block' }
  }

  const buildingsWithAds = injectAds(buildings, adInterval)

  return (
    <>
      <Box display="flex" style={{ flex: '1 1 0%' }} background="transparent">
        <motion.div
          variants={listAnimation}
          initial="show"
          animate={isListVisible || !isMobile ? 'show' : 'hidden'}
          exit="hidden"
          style={{ willChange: 'auto', flex: '1 1 0%' }}
        >
          <ListingsList
            onlyVerifiedListings={onlyVerifiedListings}
            featured={featured}
            buildings={buildings}
            buildingsWithAds={buildingsWithAds}
            loadingBuildings={loadingBuildings}
            metadata={metadata}
            markersLoaded={markersLoaded}
            data={data}
            neighborhood={neighborhood}
            city={cityData}
            cityLoading={cityLoading}
            citySlug={citySlug}
            filterSlug={filterSlug}
            filter={{ filters, setFilter, isFilterSet }}
            {...rest}
          />
        </motion.div>
      </Box>
      <AlertConfirmationModal
        isLoggedIn={isLoggedIn}
        open={modalVisible}
        onClose={() => {
          setModalVisible(false)
          closeModal()
        }}
      />
    </>
  )
}

MapList.displayName = 'MapList'

export default MapList
